import { type Category } from '@kijiji/generated/graphql-types'

import {
  adSizesMapVip,
  BASE_AD_PATH_FOR_VIP,
  VIP_GPT_ID,
} from '@/components-page/vip/advertisement/vipAdSlots'
import {
  LEGACY_MIN_DESKTOP,
  LEGACY_MIN_TABLET,
  LEGACY_MIN_TABLET_VLI,
} from '@/constants/pageSettings'
import { type AdConfig, type GptTargeting } from '@/features/advertisement/types/adConfig'
import { flattenGptArray } from '@/features/advertisement/utils/globalOptimizationTool'
import { type RouteLocale } from '@/hooks/useLocale'

/** TODO: Cleanup schema types on ANVIL */
export type CategoryPath = (Category | null)[]

const getFormattedCategoryPath = (categoryPath: CategoryPath): string => {
  const formattedPath = categoryPath.reduce((acc: string, curr) => {
    if (!curr) return acc

    return `${acc}/${curr.id}`
  }, '')

  return formattedPath
}

const getVipGptPath = ({
  routeLocale,
  categoryPath,
}: {
  routeLocale: RouteLocale
  categoryPath: CategoryPath
}) => {
  let adUnitPath = BASE_AD_PATH_FOR_VIP

  /** Should append locale to unit path */
  adUnitPath += `-${routeLocale}`

  /** Should append category IDs to unit path */
  const categoryPathString = getFormattedCategoryPath(categoryPath)
  adUnitPath += categoryPathString

  return adUnitPath
}

export const createVipGptConfig = ({
  categoryPath,
  gptTargetingValues,
  routeLocale,
  isModalVisible,
}: {
  gptTargetingValues: GptTargeting[]
  routeLocale: RouteLocale
  categoryPath: CategoryPath
  isModalVisible: boolean
}) => {
  /**
   * It will mount a path in the format:
   * /{AD_NETWORK_CODE}/{AD_PATH_BASE}{PAGE_NAME}-web-{LANG}/{ID_L1}{ID_L2}{ID_L3}`
   */
  const adUnitPath = getVipGptPath({ routeLocale, categoryPath })

  const vipGptConfig: AdConfig = {
    path: adUnitPath,
    enableLazyLoad: {
      marginPercent: 5, // 5% from the bottom of the viewport
      mobileScaling: 2,
    },
    usePrebid: false,
    useAPS: false,
    sizeMappings: {
      [VIP_GPT_ID.GPT_SPONSORED_LEFT]: [
        {
          viewPortSize: [0, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.GPT_SPONSORED_LEFT][1]],
        },
        {
          viewPortSize: [LEGACY_MIN_TABLET, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.GPT_SPONSORED_LEFT][0]],
        },
      ],
      [VIP_GPT_ID.LEADER]: [
        {
          viewPortSize: [0, 0],
          sizes: [],
        },
        {
          viewPortSize: [LEGACY_MIN_TABLET, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.LEADER][0]],
        },
        {
          viewPortSize: [LEGACY_MIN_DESKTOP, 0],
          sizes: adSizesMapVip[VIP_GPT_ID.LEADER], // Include all
        },
      ],
      [VIP_GPT_ID.GPT_SPONSORED_RIGHT]: [
        {
          viewPortSize: [0, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.GPT_SPONSORED_RIGHT][1]],
        },
        {
          viewPortSize: [LEGACY_MIN_TABLET, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.GPT_SPONSORED_RIGHT][0]],
        },
      ],
      ...(isModalVisible
        ? {
            [VIP_GPT_ID.IMAGE_GALLERY_OVERLAY]: [
              {
                viewPortSize: [0, 0],
                sizes: [adSizesMapVip[VIP_GPT_ID.IMAGE_GALLERY_OVERLAY][0]],
              },
              {
                viewPortSize: [LEGACY_MIN_TABLET_VLI, 0],
                sizes: [adSizesMapVip[VIP_GPT_ID.IMAGE_GALLERY_OVERLAY][1]],
              },
            ],
          }
        : {}),
      [VIP_GPT_ID.FOOTER]: [
        {
          viewPortSize: [0, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.FOOTER][0], adSizesMapVip[VIP_GPT_ID.FOOTER][1]],
        },
        {
          viewPortSize: [LEGACY_MIN_TABLET, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.FOOTER][2]],
        },
        {
          viewPortSize: [LEGACY_MIN_DESKTOP, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.FOOTER][2], adSizesMapVip[VIP_GPT_ID.FOOTER][3]],
        },
      ],
      [VIP_GPT_ID.R2S]: [
        {
          viewPortSize: [0, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.R2S][0], adSizesMapVip[VIP_GPT_ID.R2S][1]],
        },
        {
          viewPortSize: [LEGACY_MIN_TABLET, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.R2S][2]],
        },
        {
          viewPortSize: [LEGACY_MIN_DESKTOP, 0],
          sizes: [adSizesMapVip[VIP_GPT_ID.R2S][2], adSizesMapVip[VIP_GPT_ID.R2S][3]],
        },
      ],
    },

    targeting: flattenGptArray(gptTargetingValues),

    // slot-level settings override the global settings above
    slots: [
      {
        id: VIP_GPT_ID.GPT_SPONSORED_LEFT,
        sizeMappingName: VIP_GPT_ID.GPT_SPONSORED_LEFT,
        sizes: adSizesMapVip[VIP_GPT_ID.GPT_SPONSORED_LEFT],
        targeting: { tile: '1' },
        prebid: [],
      },
      {
        id: VIP_GPT_ID.GPT_SPONSORED_RIGHT,
        sizeMappingName: VIP_GPT_ID.GPT_SPONSORED_RIGHT,
        sizes: adSizesMapVip[VIP_GPT_ID.GPT_SPONSORED_RIGHT],
        targeting: { tile: '1' },
        prebid: [],
      },
      ...(isModalVisible
        ? [
            {
              id: VIP_GPT_ID.IMAGE_GALLERY_OVERLAY,
              sizeMappingName: VIP_GPT_ID.IMAGE_GALLERY_OVERLAY,
              sizes: adSizesMapVip[VIP_GPT_ID.IMAGE_GALLERY_OVERLAY],
              targeting: { pos: 'top', tile: '1' },
              prebid: [],
              enableLazyLoad: true,
            },
          ]
        : []),
      {
        id: VIP_GPT_ID.LEADER,
        sizeMappingName: VIP_GPT_ID.LEADER,
        sizes: adSizesMapVip[VIP_GPT_ID.LEADER],
        targeting: { tile: '4' },
        prebid: [],
      },
      {
        id: VIP_GPT_ID.FOOTER,
        sizeMappingName: VIP_GPT_ID.FOOTER,
        sizes: adSizesMapVip[VIP_GPT_ID.FOOTER],
        targeting: { pos: 'base', tile: '5' },
        prebid: [],
      },
      {
        id: VIP_GPT_ID.R2S,
        sizeMappingName: VIP_GPT_ID.R2S,
        sizes: adSizesMapVip[VIP_GPT_ID.R2S],
        targeting: { pos: 'top', tile: '4' },
        prebid: [],
      },
    ],
  }

  return vipGptConfig
}
